import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { LOGO_CHOICES } from "@/lib/constants";

const STORAGE_KEY = "kadbisnes_order_draft_v1";

const emptyAddress = {
  line1: "",
  line2: "",
  postcode: "",
  city: "",
  state: "",
  country: "Malaysia",
};

const initialState = {
  productCode: null,
  templateId: null,
  quantity: 100,
  business: {
    namaPenuh: "",
    jawatan: "",
    namaPerniagaan: "",
    bidangBisnes: "",
    telefon: "",
    whatsapp: "",
    email: "",
    website: "",
  },
  businessAddress: { ...emptyAddress },
  shippingAddress: { ...emptyAddress },
  shippingSameAsBusiness: false,
  logoChoice: LOGO_CHOICES.NONE,
  logoPreview: null, // backend-served URL of the stored logo
  logoPath: null, // durable object-storage reference
  logoFileName: null,
  informationConfirmed: false,
  directoryListingConsent: false,
  // Persisted order (MongoDB is authoritative once this is set).
  createdOrder: null,
  idempotencyKey: null,
};

const OrderContext = createContext(null);

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    return { ...initialState, ...JSON.parse(raw) };
  } catch {
    return initialState;
  }
};

export const OrderProvider = ({ children }) => {
  const [order, setOrder] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
    } catch {
      /* ignore quota errors (large logo preview) */
    }
  }, [order]);

  const value = useMemo(() => {
    const patch = (p) => setOrder((prev) => ({ ...prev, ...p }));
    return {
      order,
      patch,
      setProduct: (productCode) => patch({ productCode }),
      setTemplate: (templateId) => patch({ templateId }),
      setQuantity: (quantity) => patch({ quantity }),
      setBusiness: (p) => setOrder((prev) => ({ ...prev, business: { ...prev.business, ...p } })),
      setBusinessAddress: (p) =>
        setOrder((prev) => {
          const businessAddress = { ...prev.businessAddress, ...p };
          const shippingAddress = prev.shippingSameAsBusiness
            ? { ...businessAddress }
            : prev.shippingAddress;
          return { ...prev, businessAddress, shippingAddress };
        }),
      setShippingAddress: (p) =>
        setOrder((prev) => ({ ...prev, shippingAddress: { ...prev.shippingAddress, ...p } })),
      setShippingSame: (same) =>
        setOrder((prev) => ({
          ...prev,
          shippingSameAsBusiness: same,
          shippingAddress: same ? { ...prev.businessAddress } : prev.shippingAddress,
        })),
      reset: () => setOrder(initialState),
    };
  }, [order]);

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};

export const useOrder = () => {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error("useOrder must be used inside OrderProvider");
  return ctx;
};
